// src/admin/pages/AdminMessages.js
import React, { useState, useEffect, useContext } from "react";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { FaEnvelope } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import {
  getAdminMessages,
  deleteAdminMessage,
} from "../../services/adminMessages.api";

function AdminMessages() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { lang } = useContext(AdminLanguageContext);

  const TEXT = {
    EN: {
       title: "Messages",
  all: "All Messages",
  loading: "Loading messages...",
  empty: "No messages found.",
  viewTicket: "View Message",
      open: "New",
      pending: "Pending",
      closed: "Resolved",
      user: "User",
      subject: "Subject",
      status: "Status",
      lastMessage: "Last Message",
      date: "Date",
      view: "View",
      phone: "Phone",
      email: "Email",
      action: "Action",
      delete: "Delete",
      deleteConfirm: "Are you sure you want to delete this message?",
      noPermission: "You do not have permission to access messages.",
    },
    AR: {
        title: "الرسائل",
  all: "كل الرسائل",
  loading: "جاري تحميل الرسائل...",
  empty: "لا توجد رسائل.",
  viewTicket: "عرض الرسالة",
  deleteConfirm: "هل أنت متأكد من حذف هذه الرسالة؟",
      open: "جديدة",
      pending: "قيد الانتظار",
      closed: "تمت المعالجة",
      user: "المستخدم",
      subject: "الموضوع",
      status: "الحالة",
      lastMessage: "آخر رسالة",
      date: "التاريخ",
      view: "عرض",
      phone: "الهاتف",
      email: "البريد الإلكتروني",
      action: "إجراء",
      delete: "حذف",
      noPermission: "ليس لديك صلاحية للوصول إلى الرسائل",
    },
  }[lang];

  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
  const perms = JSON.parse(localStorage.getItem("staffPermissions") || {});
  const isAdmin = adminUser.role === "admin";
  const canAccess = isAdmin || perms.can_manage_messages;

  const [deleteId, setDeleteId] = useState(null);
const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
const pageSize = 6;

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");

  useEffect(() => {
    if (!canAccess) return;

    async function load() {
      try {
        setLoading(true);
        const list = await getAdminMessages();
        setTickets(list);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [canAccess]);

  const confirmDelete = async () => {
  try {
    await deleteAdminMessage(deleteId);

    setTickets(prev => prev.filter(x => x.id !== deleteId));

    setToast({
      type: "success",
      message:
        lang === "AR"
          ? "تم حذف الرسالة"
          : "Message deleted",
    });

    setTimeout(() => setToast(null), 2500);

  } catch (e) {
    console.error(e);
  } finally {
    setDeleteId(null);
  }
};

  const mapStatus = (s) => {
    if (s === "new") return "open";
    if (s === "in_progress") return "pending";
    return "closed";
  };

  const filtered = tickets.filter((t) =>
    statusFilter === "All" ? true : mapStatus(t.status) === statusFilter
  );

  const paginated = filtered.slice(
  (page - 1) * pageSize,
  page * pageSize
);

useEffect(() => {
  setPage(1);
}, [statusFilter]);

  const getStatusBadge = (status) => {
    if (status === "open") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 text-[12px] font-medium rounded-md bg-green-50 text-green-700 border border-green-100">
          {TEXT.open}
        </span>
      );
    }

    if (status === "pending") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 text-[12px] font-medium rounded-md bg-amber-50 text-amber-700 border border-amber-100">
          {TEXT.pending}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-2.5 py-1 text-[12px] font-medium rounded-md bg-gray-100 text-gray-900 border border-gray-200">
        {TEXT.closed}
      </span>
    );
  };

  return (
    <div className={`flex bg-[#f1f1f1] min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className={`flex-1 ${lang === "EN" ? "lg:ml-64" : ""} w-full`}>
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-4 sm:p-6">
          {!canAccess && (
            <div className="bg-red-50 border border-red-200 rounded-[14px] p-6 text-center shadow-sm">
              <p className="text-red-700 font-semibold">{TEXT.noPermission}</p>
            </div>
          )}

          {canAccess && (
            <>
              {/* HEADER */}
              <div className="flex items-center gap-2 mb-5">
                <FaEnvelope className="text-gray-900 text-[18px]" />
                <h1 className="text-[20px] font-semibold text-gray-900">
                  {TEXT.title}
                </h1>
              </div>

              {/* DESKTOP TABLE */}
              <div className="hidden md:block bg-white border border-[#e3e3e3] rounded-[14px] shadow-[0_12px_32px_rgba(0,0,0,0.10)] overflow-hidden">
                {/* TOP BAR */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-b border-[#e3e3e3]">
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: TEXT.all, value: "All" },
                      { label: TEXT.open, value: "open" },
                      { label: TEXT.pending, value: "pending" },
                      { label: TEXT.closed, value: "closed" },
                    ].map((item) => (
                      <button
                        key={item.value}
                        onClick={() => setStatusFilter(item.value)}
                        className={`px-3 py-[5px] text-[13px] rounded-md transition ${
                          statusFilter === item.value
                            ? "bg-gray-200 text-gray-900"
                            : "text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>

                  <div className="text-sm text-gray-500">
                    {filtered.length} {
  lang === "AR"
    ? (filtered.length === 1 ? "رسالة" : "رسائل")
    : (filtered.length === 1 ? "message" : "messages")
}
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table
                    dir={lang === "AR" ? "rtl" : "ltr"}
                    className="w-full text-left"
                  >
                    <thead className="bg-[#f6f6f7] text-[#6d7175] text-[13px]">
                      <tr>
                        <th className="px-4 py-3 font-medium">{TEXT.user}</th>
                        <th className="px-4 py-3 font-medium">{TEXT.phone}</th>
                        <th className="px-4 py-3 font-medium">{TEXT.email}</th>
                        <th className="px-4 py-3 font-medium">{TEXT.subject}</th>
                        <th className="px-4 py-3 font-medium">{TEXT.status}</th>
                        <th className="px-4 py-3 font-medium">{TEXT.lastMessage}</th>
                        <th className="px-4 py-3 font-medium">{TEXT.date}</th>
                        <th className="px-4 py-3 font-medium text-center">{TEXT.view}</th>
                        <th className="px-4 py-3 font-medium text-center">{TEXT.action}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading ? (
  [...Array(6)].map((_, i) => (
    <tr key={i} className="animate-pulse border-b">
      {[...Array(9)].map((_, j) => (
        <td key={j} className="px-4 py-3">
          <div className="h-3 bg-gray-200 rounded w-3/4 mx-auto"></div>
        </td>
      ))}
    </tr>
  ))
) : filtered.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="py-12 text-center">
  <div className="flex flex-col items-center gap-2 text-gray-500">
    <FaEnvelope className="text-2xl opacity-60" />
    <p className="text-sm font-medium">{TEXT.empty}</p>

    <p className="text-xs text-gray-400">
      {lang === "AR"
        ? "ستظهر الرسائل هنا عند وصولها"
        : "Messages will appear here when received"}
    </p>
  </div>
</td>
                        </tr>
                      ) : (
                        paginated.map((t) => (
                          <tr
                            key={t.id}
                            className="border-b border-[#ececec] hover:bg-[#f6f6f7] transition"
                          >
                            <td className="px-4 py-3 text-[13px] text-gray-900">
                              <div className="font-medium leading-tight">
                                {t.full_name}
                              </div>
                            </td>

                            <td className="px-4 py-3 text-[13px] text-gray-600 max-w-[140px] truncate">
                              {t.phone || "—"}
                            </td>

                            <td className="px-4 py-3 text-[13px] text-gray-600 max-w-[220px] truncate">
                              {t.email || "—"}
                            </td>

                            <td className="px-4 py-3 text-[13px] text-gray-800 max-w-[200px] truncate">
                              {t.subject}
                            </td>

                            <td className="px-4 py-3">
                              {getStatusBadge(mapStatus(t.status))}
                            </td>

                            <td className="px-4 py-3 text-[13px] text-gray-600 max-w-[280px] truncate">
                              {t.lastMessage}
                            </td>

                            <td className="px-4 py-3 text-[13px] text-gray-500 whitespace-nowrap">
                              {new Date(t.created_at).toLocaleDateString()}
                            </td>

                            <td className="px-4 py-3 text-center">
                              <button
                                className="px-3 py-1.5 text-[13px] font-medium text-gray-800 border border-gray-300 rounded-md hover:bg-gray-100 transition"
                                onClick={() => navigate(`/admin/messages/${t.id}`)}
                              >
                                {TEXT.view}
                              </button>
                            </td>

                            <td className="px-4 py-3 text-center">
                              <button
                                className="px-3 py-1.5 text-[13px] font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition"
                                onClick={() => setDeleteId(t.id)}
                              >
                                {TEXT.delete}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>

                    {!loading && filtered.length > 0 && (
                      <tfoot>
                        <tr className="bg-[#f6f6f7] border-t border-[#e3e3e3]">
                          <td
                            colSpan="9"
                            className={`px-4 py-2 ${
                              lang === "EN"
                                ? "rounded-br-[14px] rounded-bl-[14px]"
                                : "rounded-bl-[14px] rounded-br-[14px]"
                            }`}
                          >
                            <div
  className={`flex items-center justify-between text-sm text-gray-600 ${
    lang === "AR" ? "flex-row-reverse" : ""
  }`}
>

  {/* COUNT */}
  <span className="text-gray-500 text-sm">
    {filtered.length} {
      lang === "AR"
        ? (filtered.length === 1 ? "رسالة" : "رسائل")
        : (filtered.length === 1 ? "message" : "messages")
    }
  </span>

  {/* PAGINATION */}
  <div className="flex items-center gap-1">

    <button
      onClick={() => setPage(p => Math.max(1, p - 1))}
      disabled={page === 1}
      className={`w-7 h-7 flex items-center justify-center rounded-md border bg-gray-200 ${
        page === 1 ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-300"
      }`}
    >
      {"<"}
    </button>

    <button
      onClick={() => setPage(p => p + 1)}
      disabled={page * pageSize >= filtered.length}
      className={`w-7 h-7 flex items-center justify-center rounded-md border bg-gray-200 ${
        page * pageSize >= filtered.length ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-300"
      }`}
    >
      {">"}
    </button>

    <span className="mx-2 text-gray-600 font-medium">
      {page}-{Math.ceil(filtered.length / pageSize) || 1}
    </span>

  </div>
</div>
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              {/* MOBILE VIEW */}
              <div className="md:hidden space-y-3">
                {/* FILTER PILLS */}
                <div className="bg-white border border-[#e3e3e3] rounded-[14px] shadow-sm p-3">
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: TEXT.all, value: "All" },
                      { label: TEXT.open, value: "open" },
                      { label: TEXT.pending, value: "pending" },
                      { label: TEXT.closed, value: "closed" },
                    ].map((item) => (
                      <button
                        key={item.value}
                        onClick={() => setStatusFilter(item.value)}
                        className={`px-3 py-[5px] text-[13px] rounded-md transition ${
                          statusFilter === item.value
                            ? "bg-gray-200 text-gray-900"
                            : "text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

               {loading ? (
  [...Array(4)].map((_, i) => (
    <div key={i} className="bg-white p-4 rounded-xl shadow animate-pulse space-y-3">
      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      <div className="h-3 bg-gray-200 rounded w-3/4"></div>
      <div className="h-3 bg-gray-200 rounded w-2/3"></div>
    </div>
  ))
) : filtered.length === 0 ? (
                 <div className="bg-white p-6 rounded-xl shadow text-center">
  <div className="flex flex-col items-center gap-2 text-gray-500">
    <FaEnvelope className="text-2xl opacity-60" />
    <p className="text-sm font-medium">{TEXT.empty}</p>
  </div>
</div>
                ) : (
                  paginated.map((t) => (
                    <div
                      key={t.id}
                      className="bg-white rounded-xl shadow p-4 space-y-3 border border-[#e8e8e8]"
                    >
                      {/* HEADER */}
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-[14px] text-gray-900 truncate">
                            {t.full_name}
                          </div>
                          <div className="text-xs text-gray-400 truncate">
                            {t.email || t.phone || "—"}
                          </div>
                        </div>

                        <div>{getStatusBadge(mapStatus(t.status))}</div>
                      </div>

                      {/* DETAILS */}
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between gap-3">
                          <span className="text-gray-500">{TEXT.subject}</span>
                          <span className="font-medium text-gray-900 text-right max-w-[65%] truncate">
                            {t.subject}
                          </span>
                        </div>

                        <div className="flex justify-between gap-3">
                          <span className="text-gray-500">{TEXT.date}</span>
                          <span className="text-gray-900">
                            {new Date(t.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      {/* LAST MESSAGE */}
                      <div className="text-sm text-gray-600 border-t pt-3 line-clamp-2">
                        {t.lastMessage}
                      </div>

                      {/* ACTIONS */}
                      <div className="flex justify-end gap-2 pt-2 border-t">
                        <button
                          className="px-3 py-1.5 text-sm border rounded-md text-gray-800 hover:bg-gray-100"
                          onClick={() => navigate(`/admin/messages/${t.id}`)}
                        >
                          {TEXT.viewTicket}
                        </button>

                        <button
                          className="px-3 py-1.5 text-sm border border-red-200 rounded-md text-red-600 hover:bg-red-50"
                          onClick={() => setDeleteId(t.id)}
                        >
                          {TEXT.delete}
                        </button>
                      </div>
                    </div>
                  ))
                                )}
                
                {/* ✅ MOBILE PAGINATION (ADD HERE) */}
                {!loading && filtered.length > 0 && (
                  <div className="sticky bottom-0 bg-white border-t p-3 flex items-center justify-between text-sm">

                    <span className="text-xs text-gray-500">
                      {page} / {Math.ceil(filtered.length / pageSize) || 1}
                    </span>

                    <div className="flex items-center gap-2">

                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-3 py-1.5 rounded-md border bg-white disabled:opacity-40"
                      >
                        {lang === "AR" ? "السابق" : "Prev"}
                      </button>

                      <button
                        onClick={() => setPage(p => p + 1)}
                        disabled={page * pageSize >= filtered.length}
                        className="px-3 py-1.5 rounded-md border bg-white disabled:opacity-40"
                      >
                        {lang === "AR" ? "التالي" : "Next"}
                      </button>

                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          {deleteId !== null && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">

    <div className="w-[380px] bg-white rounded-[14px] shadow-xl overflow-hidden">

      {/* HEADER */}
      <div className="px-5 py-4 border-b bg-[#f6f6f7] flex items-center gap-3">
        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-50 border">
          <FaEnvelope className="text-red-600 text-sm" />
        </div>

        <h2 className="text-[15px] font-semibold">
          {lang === "AR" ? "حذف الرسالة؟" : "Delete message?"}
        </h2>
      </div>

      {/* BODY */}
      <div className="px-5 py-5 text-sm text-gray-600">
        {lang === "AR"
          ? "لا يمكن التراجع عن هذا الإجراء."
          : "This action cannot be undone."}
      </div>

      {/* FOOTER */}
      <div className="px-5 py-4 border-t flex justify-end gap-3 bg-[#f6f6f7]">

        <button
          onClick={() => setDeleteId(null)}
          className="h-[36px] px-4 border rounded-lg bg-white"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          onClick={confirmDelete}
          className="h-[36px] px-4 bg-red-600 text-white rounded-lg"
        >
          {lang === "AR" ? "حذف" : "Delete"}
        </button>

      </div>
    </div>
  </div>
)}
{toast && (
  <div className="fixed bottom-5 right-5 z-[9999] animate-fadeSlide">
    <div className="
      flex items-center gap-3
      px-4 py-3
      bg-[#202223]
      text-white
      rounded-lg
      shadow-[0_10px_25px_rgba(0,0,0,0.2)]
       text-sm font-medium
      min-w-[220px]
    ">

      <div className="
        w-5 h-5 flex items-center justify-center
        rounded-full bg-white/20 text-xs
      ">
        ✓
      </div>

      <span>{toast.message}</span>
    </div>
  </div>
)}
        </main>
      </div>
    </div>
  );
}

export default AdminMessages;